/**
 * Spoil Reconciliation — shared derivation
 * ========================================
 *
 * `/roads-sewers/spoil-reconciliation` is a volume balance, not a priced bill:
 * topsoil strip vs re-use and subsoil cut vs fill, per area of the scheme, in m³.
 * Most rows are *calculated* from other pages (Roads' carriageway summary and
 * cut/fill sections, Storm's Ponds & Swales build-up, the Foul/Storm surplus
 * tables); the rest are the estimator's own inputs, saved in
 * `state.spoilReconciliation.config`.
 *
 * That derivation used to live inside `SpoilReconciliation.tsx`. It moved here so
 * the page and the Review & Calculate Run Matrix's "Spoil" section read exactly
 * the same numbers — the page is the source of truth for the formulas, this file
 * is the single implementation of them.
 */

import type {
  FoulBuildUpRowData,
  LateralConnection,
  Manhole,
  GullyConnection,
  RoadsData,
  SewerPipeRun,
  SpoilReconciliationData,
  StormSewerRun,
} from '../types/tender.types';
import { buildFoulSurplusBackfillData } from './foulSummaryUtils';
import { buildStormSurplusBackfillData } from './stormSummaryUtils';
import { PONDS_ROWS } from './stormBuildUpPonds';
import { SWALES_ROWS } from './stormBuildUpSwales';
import { calcRoadTopsoilExcavation, calcVergeTotal, getFootpathsConfig } from './roadsSummaryCalculations';

const PONDS_SWALES_ROWS = [...PONDS_ROWS, ...SWALES_ROWS];

/** The subset of `SpoilReconciliationData` the estimator types in by hand. */
export type SpoilUserInputs = Pick<
  SpoilReconciliationData,
  | 'topsoilPOSStrip' | 'topsoilAttenuationTanksStrip' | 'topsoilCulvertsStrip'
  | 'topsoilPOSReuse' | 'topsoilAttenuationTanksReuse' | 'topsoilCulvertsReuse'
  | 'subsoilPOSCut' | 'subsoilAttenuationTanksCut' | 'subsoilCulvertsCut' | 'subsoilMainsServicesCut'
  | 'subsoilPOSFill' | 'subsoilAttenuationTanksFill' | 'subsoilCulvertsFill' | 'subsoilMainsServicesFill'
  | 'subsoilRoadFill'
>;

export const emptySpoilUserInputs = (): SpoilUserInputs => ({
  topsoilPOSStrip: 0, topsoilAttenuationTanksStrip: 0, topsoilCulvertsStrip: 0,
  topsoilPOSReuse: 0, topsoilAttenuationTanksReuse: 0, topsoilCulvertsReuse: 0,
  subsoilPOSCut: 0, subsoilAttenuationTanksCut: 0, subsoilCulvertsCut: 0, subsoilMainsServicesCut: 0,
  subsoilPOSFill: 0, subsoilAttenuationTanksFill: 0, subsoilCulvertsFill: 0, subsoilMainsServicesFill: 0,
  subsoilRoadFill: 0,
});

export interface SpoilReconciliationSource {
  roads?: RoadsData;
  foul?: { pipeRuns: SewerPipeRun[]; manholes: Manhole[]; laterals: LateralConnection[] };
  storm?: {
    pipeRuns: StormSewerRun[];
    manholes: Manhole[];
    laterals: LateralConnection[];
    gullies: GullyConnection[];
  };
  /** `state.pondsSwales.table` — Ponds & Swales build-up quantities. */
  pondsSwalesTable?: Record<string, FoulBuildUpRowData>;
  inputs: SpoilUserInputs;
}

/** One area of the scheme. `null` = that column isn't measured for this area. */
export interface SpoilAreaRow {
  key: string;
  label: string;
  topsoilStrip: number | null;
  topsoilReuse: number | null;
  subsoilCut: number | null;
  subsoilFill: number | null;
  /** Where the numbers come from — shown as the row's note in the matrix. */
  origin: string;
}

export interface SpoilReconciliationSummary {
  /** Every named value `SpoilReconciliation.tsx` renders, in its own naming. */
  derived: {
    roadTopsoilStrip: number;
    roadTopsoilReuse: number;
    topsoilPondsStrip: number;
    topsoilPondsReuse: number;
    subsoilRoadCut: number;
    subsoilPathsCut: number;
    subsoilPathsFill: number;
    subsoilFoulCut: number;
    subsoilStormCut: number;
    subsoilPondsCut: number;
    subsoilPondsFill: number;
    totalTopsoilStrip: number;
    totalTopsoilReuse: number;
    topsoilSurplusDeficit: number;
    totalSubsoilCut: number;
    totalSubsoilFill: number;
    subsoilSurplusDeficit: number;
  };
  /** Per-area rows, for the Run Matrix's Spoil section. */
  rows: SpoilAreaRow[];
}

const to3dp = (n: number): number => parseFloat(n.toFixed(3));

export const buildSpoilReconciliationSummary = (
  source: SpoilReconciliationSource,
): SpoilReconciliationSummary => {
  const { roads, foul, storm, pondsSwalesTable, inputs } = source;
  const cutFillSections = roads?.cutFillSections.table || [];

  // ── Roads ────────────────────────────────────────────────────────────────
  // Same value as /roads-sewers/roads → Carriageway Summary's "Excavate to reduce
  // levels to take up Xmm top soil, deposit onsite" row, summed across every road.
  const roadTopsoilStrip = to3dp(calcRoadTopsoilExcavation(roads).total);
  // Re-use = total verge area × the verge topsoil thickness configured on
  // Footpaths — Construction Configuration (default 0.15m) — the stripped
  // topsoil is spread back over the verges at that thickness, rather than an
  // arbitrary 15% share of the whole strip volume.
  const vergeTotal = calcVergeTotal(roads);
  const vergeTopsoilThickness = getFootpathsConfig(roads).verge.topsoil || 0;
  const roadTopsoilReuse = to3dp(vergeTotal * vergeTopsoilThickness);

  const subsoilRoadCut = to3dp(cutFillSections.reduce((sum, s) => sum + (s.cutVolume || 0), 0));
  const subsoilPathsCut = to3dp(
    cutFillSections.reduce((sum, s) => sum + (s.footpathsCutVolume || 0), 0),
  );
  // footpathsFillVolume is stored positive when computed on the Roads page
  // (RoadsTakeoff.tsx applies Math.abs to the depth), but Excel-imported
  // cutFillSections can carry the sheet's own negative-fill convention — Math.abs
  // per section keeps this always positive regardless of source, and prevents
  // positive/negative sections cancelling each other out.
  const subsoilPathsFill = to3dp(
    cutFillSections.reduce((sum, s) => sum + Math.abs(s.footpathsFillVolume || 0), 0),
  );

  // ── Foul / Storm ─────────────────────────────────────────────────────────
  // Grand total Surplus from each discipline's Summary tab Surplus table
  // (Runs + Lateral + Manholes [+ Gullies], per pipe run).
  const subsoilFoulCut = to3dp(
    buildFoulSurplusBackfillData(
      foul?.pipeRuns || [],
      foul?.manholes || [],
      foul?.laterals || [],
    ).surplusTotal,
  );
  const subsoilStormCut = to3dp(
    buildStormSurplusBackfillData(
      storm?.pipeRuns || [],
      storm?.manholes || [],
      storm?.laterals || [],
      storm?.gullies || [],
    ).surplusTotal,
  );

  // ── Ponds & Swales build-up ──────────────────────────────────────────────
  const pondsTable = pondsSwalesTable || {};
  const sumPondRows = (suffixes: string[]): number =>
    PONDS_SWALES_ROWS.filter((r) => suffixes.some((sfx) => r.id.endsWith(sfx))).reduce(
      (sum, r) => sum + (pondsTable[r.id]?.quant || 0),
      0,
    );

  const topsoilPondsStrip = to3dp(sumPondRows(['_excTop']));
  const topsoilPondsReuse = to3dp(sumPondRows(['_topsoilSiteWon', '_topsoilImported']) * 0.15);
  const subsoilPondsCut = to3dp(sumPondRows(['_excLvl']));
  const subsoilPondsFill = to3dp(sumPondRows(['_fill']));

  // ── Totals ───────────────────────────────────────────────────────────────
  const totalTopsoilStrip = to3dp(
    roadTopsoilStrip + topsoilPondsStrip + inputs.topsoilPOSStrip +
      inputs.topsoilAttenuationTanksStrip + inputs.topsoilCulvertsStrip,
  );
  const totalTopsoilReuse = to3dp(
    roadTopsoilReuse + topsoilPondsReuse + inputs.topsoilPOSReuse +
      inputs.topsoilAttenuationTanksReuse + inputs.topsoilCulvertsReuse,
  );
  const topsoilSurplusDeficit = to3dp(totalTopsoilStrip - totalTopsoilReuse);

  const totalSubsoilCut = to3dp(
    subsoilRoadCut + subsoilPathsCut + subsoilFoulCut + subsoilStormCut + subsoilPondsCut +
      inputs.subsoilPOSCut + inputs.subsoilAttenuationTanksCut + inputs.subsoilCulvertsCut +
      inputs.subsoilMainsServicesCut,
  );
  const totalSubsoilFill = to3dp(
    inputs.subsoilRoadFill + subsoilPathsFill + subsoilPondsFill + inputs.subsoilPOSFill +
      inputs.subsoilAttenuationTanksFill + inputs.subsoilCulvertsFill +
      inputs.subsoilMainsServicesFill,
  );
  const subsoilSurplusDeficit = to3dp(totalSubsoilCut - totalSubsoilFill);

  // ── Per-area rows ────────────────────────────────────────────────────────
  // The page measures topsoil for roads and footpaths together on one line but
  // splits subsoil into Road and Paths, so the combined topsoil figure sits on
  // the Roads row and the Footpaths row carries subsoil only.
  const rows: SpoilAreaRow[] = [
    {
      key: 'roads', label: 'Roads', origin: 'Roads → Carriageway Summary / Cut & Fill sections',
      topsoilStrip: roadTopsoilStrip, topsoilReuse: roadTopsoilReuse,
      subsoilCut: subsoilRoadCut, subsoilFill: inputs.subsoilRoadFill,
    },
    {
      key: 'footpaths', label: 'Footpaths', origin: 'Roads → Cut & Fill sections (topsoil measured with Roads)',
      topsoilStrip: null, topsoilReuse: null,
      subsoilCut: subsoilPathsCut, subsoilFill: subsoilPathsFill,
    },
    {
      key: 'ponds', label: 'Ponds & Swales', origin: 'Storm Sewers → Ponds & Swales build-up',
      topsoilStrip: topsoilPondsStrip, topsoilReuse: topsoilPondsReuse,
      subsoilCut: subsoilPondsCut, subsoilFill: subsoilPondsFill,
    },
    {
      key: 'foul', label: 'Foul Sewers', origin: 'Foul Sewers → Foul Summary surplus table',
      topsoilStrip: null, topsoilReuse: null,
      subsoilCut: subsoilFoulCut, subsoilFill: null,
    },
    {
      key: 'storm', label: 'Storm Sewers', origin: 'Storm Sewers → Storm Summary surplus table',
      topsoilStrip: null, topsoilReuse: null,
      subsoilCut: subsoilStormCut, subsoilFill: null,
    },
    {
      key: 'pos', label: 'POS', origin: 'Entered on the Spoil Reconciliation page',
      topsoilStrip: inputs.topsoilPOSStrip, topsoilReuse: inputs.topsoilPOSReuse,
      subsoilCut: inputs.subsoilPOSCut, subsoilFill: inputs.subsoilPOSFill,
    },
    {
      key: 'attenuation', label: 'Attenuation Tanks', origin: 'Entered on the Spoil Reconciliation page',
      topsoilStrip: inputs.topsoilAttenuationTanksStrip, topsoilReuse: inputs.topsoilAttenuationTanksReuse,
      subsoilCut: inputs.subsoilAttenuationTanksCut, subsoilFill: inputs.subsoilAttenuationTanksFill,
    },
    {
      key: 'culverts', label: 'Culverts', origin: 'Entered on the Spoil Reconciliation page',
      topsoilStrip: inputs.topsoilCulvertsStrip, topsoilReuse: inputs.topsoilCulvertsReuse,
      subsoilCut: inputs.subsoilCulvertsCut, subsoilFill: inputs.subsoilCulvertsFill,
    },
    {
      key: 'mains', label: 'Mains Services', origin: 'Entered on the Spoil Reconciliation page',
      topsoilStrip: null, topsoilReuse: null,
      subsoilCut: inputs.subsoilMainsServicesCut, subsoilFill: inputs.subsoilMainsServicesFill,
    },
  ];

  return {
    derived: {
      roadTopsoilStrip, roadTopsoilReuse, topsoilPondsStrip, topsoilPondsReuse,
      subsoilRoadCut, subsoilPathsCut, subsoilPathsFill, subsoilFoulCut, subsoilStormCut,
      subsoilPondsCut, subsoilPondsFill,
      totalTopsoilStrip, totalTopsoilReuse, topsoilSurplusDeficit,
      totalSubsoilCut, totalSubsoilFill, subsoilSurplusDeficit,
    },
    rows,
  };
};
